/**
 * ContextoOrganizacao - Contexto React para gerenciar organizações (Multi-Tenant)
 *
 * CONTEXTO DE NEGÓCIO:
 * Gerencia estado global de organizações, permitindo que o usuário:
 * - Visualize as organizações das quais é membro
 * - Selecione uma organização ativa para filtrar processos/documentos
 * - Crie novas organizações
 * - Gerencie membros e convites
 *
 * IMPLEMENTAÇÃO:
 * - Depende do ContextoAutenticacao para saber o usuário logado
 * - Persiste organização selecionada em localStorage
 * - Carrega organizações automaticamente quando o usuário faz login
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import type {
  Organizacao,
  DadosCriacaoOrganizacao,
  RoleOrganizacao,
  EstadoOrganizacao,
  AcoesOrganizacao,
} from '../tipos/tiposOrganizacao';
import {
  listarMinhasOrganizacoes,
  criarOrganizacao as apiCriarOrganizacao,
  sairDaOrganizacao as apiSairDaOrganizacao,
} from '../servicos/servicoOrganizacao';
import { useAutenticacao } from './ContextoAutenticacao';

// ===== Constantes =====

const LOCALSTORAGE_ORG_KEY = '@workforce-juridico:organizacao_id';

// ===== Tipos do Contexto =====

interface ValoresContextoOrganizacao extends EstadoOrganizacao, AcoesOrganizacao {}

// ===== Contexto =====

const ContextoOrganizacao = createContext<ValoresContextoOrganizacao | undefined>(undefined);

// ===== Provider =====

interface PropriedadesProvedor {
  children: ReactNode;
}

export const ProvedorOrganizacao = ({ children }: PropriedadesProvedor): JSX.Element => {
  const { usuario, estaAutenticado, token } = useAutenticacao();
  
  // Estado
  const [organizacoes, setOrganizacoes] = useState<Organizacao[]>([]);
  const [organizacaoAtual, setOrganizacaoAtual] = useState<Organizacao | null>(null);
  const [roleAtual, setRoleAtual] = useState<RoleOrganizacao | null>(null);
  const [carregando, setCarregando] = useState<boolean>(false);

  // ===== Carregar Organizações =====
  
  const carregarOrganizacoes = useCallback(async () => {
    if (!estaAutenticado || !token) {
      setOrganizacoes([]);
      setOrganizacaoAtual(null);
      setRoleAtual(null);
      return;
    }

    setCarregando(true);
    try {
      const orgs = await listarMinhasOrganizacoes();
      setOrganizacoes(orgs);

      // Tentar restaurar organização salva
      const orgIdSalvo = localStorage.getItem(LOCALSTORAGE_ORG_KEY);
      if (orgIdSalvo) {
        const orgEncontrada = orgs.find((o) => o.id === orgIdSalvo);
        if (orgEncontrada) {
          setOrganizacaoAtual(orgEncontrada);
          setRoleAtual(orgEncontrada.meu_role || null);
        } else {
          // Organização salva não existe mais, limpar
          localStorage.removeItem(LOCALSTORAGE_ORG_KEY);
          // Se tem organizações, selecionar a primeira
          if (orgs.length > 0) {
            setOrganizacaoAtual(orgs[0]);
            setRoleAtual(orgs[0].meu_role || null);
            localStorage.setItem(LOCALSTORAGE_ORG_KEY, orgs[0].id);
          }
        }
      } else if (orgs.length > 0) {
        // Nenhuma org salva, selecionar a primeira se existir
        setOrganizacaoAtual(orgs[0]);
        setRoleAtual(orgs[0].meu_role || null);
        localStorage.setItem(LOCALSTORAGE_ORG_KEY, orgs[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
      setOrganizacoes([]);
    } finally {
      setCarregando(false);
    }
  }, [estaAutenticado, token]);

  // Carregar organizações quando autenticar
  useEffect(() => {
    if (estaAutenticado && token) {
      carregarOrganizacoes();
    } else {
      // Limpar estado quando deslogar
      setOrganizacoes([]);
      setOrganizacaoAtual(null);
      setRoleAtual(null);
    }
  }, [estaAutenticado, token, carregarOrganizacoes]);

  // ===== Selecionar Organização =====
  
  const selecionarOrganizacao = useCallback((organizacaoId: string | null) => {
    if (organizacaoId === null) {
      setOrganizacaoAtual(null);
      setRoleAtual(null);
      localStorage.removeItem(LOCALSTORAGE_ORG_KEY);
      return;
    }

    const org = organizacoes.find((o) => o.id === organizacaoId);
    if (org) {
      setOrganizacaoAtual(org);
      setRoleAtual(org.meu_role || null);
      localStorage.setItem(LOCALSTORAGE_ORG_KEY, organizacaoId);
    }
  }, [organizacoes]);

  // ===== Criar Organização =====
  
  const criarOrganizacao = useCallback(async (dados: DadosCriacaoOrganizacao): Promise<Organizacao> => {
    const novaOrg = await apiCriarOrganizacao(dados);
    
    // Adicionar à lista local
    setOrganizacoes((prev) => [...prev, novaOrg]);
    
    // Selecionar a nova organização automaticamente
    setOrganizacaoAtual(novaOrg);
    setRoleAtual('owner');
    localStorage.setItem(LOCALSTORAGE_ORG_KEY, novaOrg.id);
    
    return novaOrg;
  }, []);

  // ===== Sair da Organização =====
  
  const sairDaOrganizacao = useCallback(async (organizacaoId: string): Promise<void> => {
    await apiSairDaOrganizacao(organizacaoId);
    
    // Remover da lista local
    setOrganizacoes((prev) => prev.filter((o) => o.id !== organizacaoId));
    
    // Se era a org atual, limpar seleção
    if (organizacaoAtual?.id === organizacaoId) {
      const restantes = organizacoes.filter((o) => o.id !== organizacaoId);
      if (restantes.length > 0) {
        setOrganizacaoAtual(restantes[0]);
        setRoleAtual(restantes[0].meu_role || null);
        localStorage.setItem(LOCALSTORAGE_ORG_KEY, restantes[0].id);
      } else {
        setOrganizacaoAtual(null);
        setRoleAtual(null);
        localStorage.removeItem(LOCALSTORAGE_ORG_KEY);
      }
    }
  }, [organizacaoAtual, organizacoes]);

  // ===== Valor do Contexto =====
  
  const valor: ValoresContextoOrganizacao = {
    // Estado
    organizacaoAtual,
    organizacoes,
    roleAtual,
    carregando,
    // Ações
    carregarOrganizacoes,
    selecionarOrganizacao,
    criarOrganizacao,
    sairDaOrganizacao,
  };

  return (
    <ContextoOrganizacao.Provider value={valor}>
      {children}
    </ContextoOrganizacao.Provider>
  );
};

// ===== Hook =====

export const useOrganizacao = (): ValoresContextoOrganizacao => {
  const context = useContext(ContextoOrganizacao);
  if (context === undefined) {
    throw new Error('useOrganizacao deve ser usado dentro de um ProvedorOrganizacao');
  }
  return context;
};

export { ContextoOrganizacao };
